import {
  ConversationHandover,
  Message,
  TimelineItem,
} from '@/features/messages/messages.types';
import { ExtendedTimelineItem, DateGroup } from '../conversations.types';

/**
 * Validates and normalizes a date string
 * Returns a valid ISO string or current time as fallback
 */
export const validateAndNormalizeDate = (
  dateString: string | null | undefined,
): string => {
  if (!dateString || dateString === '') {
    console.warn('Empty date detected, using current time as fallback');
    return new Date().toISOString();
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.warn(
      'Invalid date detected:',
      dateString,
      'Using current time as fallback',
    );
    return new Date().toISOString();
  }

  return date.toISOString();
};

/**
 * Sorts timeline items by timestamp in ascending order (oldest first)
 * Uses UTC timestamps for consistent sorting across timezones
 */
export const sortMessagesByTimestamp = (
  items: ExtendedTimelineItem[],
): ExtendedTimelineItem[] => {
  return [...items].sort((a, b) => {
    const timestampA = new Date(a.created_at).getTime();
    const timestampB = new Date(b.created_at).getTime();
    return timestampA - timestampB;
  });
};

/**
 * Deduplicates messages using a Set-based approach for O(n) performance
 * Handles multiple deduplication strategies: ID, tempId, and content matching
 */
export const deduplicateMessages = (
  timeline: TimelineItem[],
  pendingMessages: Message[],
  conversationId: string,
): Message[] => {
  // Create efficient lookup sets
  const deliveredIds = new Set<string>();
  const deliveredTempIds = new Set<string>();
  const deliveredContents = new Map<string, number>(); // content -> timestamp

  // Index all delivered messages
  timeline.forEach((item) => {
    if (item.type === 'message') {
      const message = item.content as Message;
      deliveredIds.add(message.id);

      if (message._tempId) {
        deliveredTempIds.add(message._tempId);
      }

      if (message.content) {
        const timestamp = new Date(message.created_at).getTime();
        deliveredContents.set(message.content, timestamp);
      }
    }
  });

  return pendingMessages.filter((message) => {
    // Only include messages for this conversation
    if (message.conversation_id !== conversationId) return false;

    // Check direct ID match
    if (deliveredIds.has(message.id)) return false;

    // Check tempId match
    if (message._tempId && deliveredIds.has(message._tempId)) return false;

    // Check if message ID matches any delivered tempId
    if (deliveredTempIds.has(message.id)) return false;

    // For attachment messages, check by attachment URLs
    if (message.attachments?.length) {
      const hasMatchingAttachment = timeline.some((item) => {
        if (item.type !== 'message') return false;
        const deliveredMsg = item.content as Message;

        return deliveredMsg.attachments?.some((deliveredAttachment) =>
          message.attachments!.some(
            (attachment) =>
              attachment.file_url === deliveredAttachment.file_url,
          ),
        );
      });

      if (hasMatchingAttachment) return false;
    }

    // For text messages, check content with time proximity
    if (message.content && deliveredContents.has(message.content)) {
      const messageTime = new Date(message.created_at).getTime();
      const deliveredTime = deliveredContents.get(message.content)!;

      // Consider it a duplicate if within 2 seconds
      if (Math.abs(messageTime - deliveredTime) < 2000) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Groups timeline items by date for better organization
 * Returns an array of date groups sorted chronologically
 */
export const groupMessagesByDate = (
  items: ExtendedTimelineItem[],
): DateGroup[] => {
  const groupMap = new Map<string, ExtendedTimelineItem[]>();

  items.forEach((item) => {
    // change the created_at to the handover_time because this created "now" and we need rewrite that
    if ('handover_time' in (item.content as ConversationHandover)) {
      const date = (item.content as ConversationHandover).handover_time;
      item.created_at = date;
    }

    // TODO: Fix Type here
    const recordDate =
      'handover_time' in (item.content as ConversationHandover)
        ? (item.content as ConversationHandover).handover_time
        : item.created_at;
    const date = new Date(recordDate).toDateString();

    if (!groupMap.has(date)) {
      groupMap.set(date, []);
    }
    groupMap.get(date)!.push(item);
  });

  // Convert to array and sort by date
  const result = Array.from(groupMap.entries())
    .map(([date, items]) => ({ date, items }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return result;
};

/**
 * Formats date strings for display in the UI
 */
export const formatDateHeader = (dateStr: string): string => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }
};

/**
 * Converts a Message to a TimelineItem format
 */
export const messageToTimelineItem = (
  message: Message,
): ExtendedTimelineItem => ({
  id: message.id,
  type: 'message',
  created_at: message.created_at,
  content: message,
  _status: message._status,
});

/**
 * Filters timeline items to only include those belonging to a specific conversation
 */
export const filterTimelineByConversation = (
  timeline: TimelineItem[],
  conversationId: string,
): TimelineItem[] => {
  return timeline.filter((item) => {
    if (item.type === 'message') {
      const message = item.content as Message;
      return message.conversation_id === conversationId;
    }
    if (item.type === 'handover') {
      const handover = item.content as ConversationHandover;
      return handover.conversation_id === conversationId;
    }
    return false;
  });
};
